/**
 * Default: Modules > Clinic Management > Filter
 *
 * @copyright 2025 i-fabrik GmbH
 * @author Tobias Garz
 */

import SelectorEngine from '../../../shared/dom/selector-engine';
import EventHandler from '../../../shared/dom/event-handler';
import Manipulator from '../../../shared/dom/manipulator';
import Data from '../../../shared/dom/data';

import {
	lockBodyScrolling,
	unlockBodyScrolling
} from '../../../shared/utils/scroll';

import {initPageBackdrop} from '../../layout';

// -------
// Private
// -------

const NAME     = 'clinic-management';
const DATA_KEY = `ifab.${NAME}`;

/**
 * @param {HTMLElement} element
 * @constructor
 */
class GlobalFacilityFilter {
	constructor(element) {
		// Wurde Element schon initialisiert?
		if (Data.get(element, `${DATA_KEY}.initialized`)) {
			return element;
		}

		this._container       = element;
		this._closeBtn        = SelectorEngine.findOne(`.button.-close`, this._container);
		this._triggerBtn      = SelectorEngine.findOne(`.button[data-action]`, this._container);
		this._backBtn         = Manipulator.createElementFrom(`<button type="button" class="button -back"/>`);
		this._resultContainer = SelectorEngine.findOne(`.filter-result`, this._container);
		this.activeData       = {
			step : false,
			state: null
		};
		this.actions          = {
			states    : Manipulator.getDataAttribute(this._triggerBtn, 'action') ? Manipulator.getDataAttribute(this._triggerBtn, 'action') : false,
			facilities: null
		};
		this.cache            = {
			tplStatesList    : null,
			tplFacilitiesList: null
		};
		this.l10n             = {
			FACILITY: window.pageInstance.tm.translate('facility')
		};

		this.registerEvents();

		Data.set(element, `${DATA_KEY}.initialized`, true);
	}

	registerEvents() {
		// Click on trigger button for first appearance
		EventHandler.on(this._triggerBtn, 'click', async () => {
			lockBodyScrolling(this._container);
			initPageBackdrop(true);
			this._pendingMode('true');

			await this.renderStep('states');

			Manipulator.addClass(this._container, '-active');
			Data.set(this._container, `${DATA_KEY}.visible`, true);
		});

		// Click on back button
		EventHandler.on(this._backBtn, 'click', async () => {
			event.stopPropagation();

			this._backAction();
		});

		// Close Button
		EventHandler.on(this._closeBtn, 'click', (event) => {
			this._pendingMode('true');
			this._closeFilter();
		});

		// Outside Click to close
		EventHandler.on(document, 'click', (event) => {
			if(!this.activeData.step) {
				return;
			}

			if (!this._resultContainer.contains(event.target) && !this._triggerBtn.contains(event.target)) {
				this._pendingMode('true');
				this._closeFilter();
			}
		});
	}

	_pendingMode(mode) {
		if(mode) {
			Data.set(this._container, `${DATA_KEY}.pending`, true);

			Manipulator.addClass(this._resultContainer, '-is-waiting');
		} else {
			Data.set(this._container, `${DATA_KEY}.pending`, false);

			Manipulator.removeClass(this._resultContainer, '-is-waiting');
		}
	}

	async _backAction() {
		this._pendingMode('true');

		await this.renderStep('states');
	}

	_closeFilter() {
		if(Manipulator.getDataAttribute(this._resultContainer, 'step')) {
			Manipulator.removeDataAttribute(this._resultContainer, 'step');
		}

		this.activeData.step  = false;
		this.activeData.state = null;
		Manipulator.removeClass(this._container, '-active');
		Data.set(this._container, `${DATA_KEY}.visible`, false);

		unlockBodyScrolling(this._container);
		initPageBackdrop(false);
	}

	async renderStates() {
		if(this.cache.tplStatesList) {
			return this.cache.tplStatesList;
		}

		let entries = await this.getData('states');

		if(entries && entries.states) {
			this.cache.tplStatesList = Manipulator.createElementFrom(`<div class="states-group"/>`);

			let tplState = '';

			for (const [key, value] of Object.entries(entries.states)) {
				tplState = `<div class="state"><h4 class="title"><a rel="nofollow" href="#" data-action="${value.link}">${value.title}</a></h4><figure class="figure"><picture class="picture"><img src="${value.image}"/></picture></figure></div>`;

				tplState = Manipulator.createElementFrom(tplState);

				// render facilities on state click
				EventHandler.on(tplState, 'click', async (event) => {
					event.preventDefault();

					this._pendingMode(true);

					const _LINK = SelectorEngine.findOne('a[data-action]', event.delegateTarget);

					if(!_LINK) {
						return;
					}
					this.actions.facilities = Manipulator.getDataAttribute(_LINK, 'action');
					this.activeData.state   = value.title;

					await this.renderStep('facilities');
				});

				Manipulator.elementAppend(tplState, this.cache.tplStatesList);
			}

			return this.cache.tplStatesList;

		} else {
			return false;
		}
	}

	async renderFacilites() {
		let entries = await this.getData('facilities');

		this._backBtn.innerHTML = '';

		if(entries && entries.institutions) {
			let tplFacility = '';

			this.cache.tplFacilitiesList   = Manipulator.createElementFrom(`<div class="facilities-group"/>`);

			// create and implement back button
			let tplBackLinkLabel = Manipulator.createElementFrom(`<span class="button__label">${this.l10n.FACILITY} in ${this.activeData.state}</span>`);
			Manipulator.elementAppend(tplBackLinkLabel, this._backBtn);
			Manipulator.elementAppend(this._backBtn, this.cache.tplFacilitiesList);

			for (const [key, value] of Object.entries(entries.institutions)) {

				tplFacility = `<div class="facility"><a href="${value.link}">${value.title}</a></div>`;

				Manipulator.elementAppend(Manipulator.createElementFrom(tplFacility), this.cache.tplFacilitiesList);
			}

			return this.cache.tplFacilitiesList;

		} else {
			return false;
		}
	}

	async getData (step= this.activeData.step) {
		try {
			if(this.actions[step]) {
				return await getJson(this.actions[step]);
			} else {
				console.error('Error - get data:', `The step "${step}" does not exist.`);

				return false;
			}
		} catch (error) {
			console.error('Error - get data:', error.message);

			return false;
		}
	}

	async renderStep(step) {
		let template = null;

		if(step === this.activeData.step) {
			this._pendingMode(false);

			return;
		}

		switch (step) {
			case 'states': {
				template = await this.renderStates();
				break;
			}
			case "facilities": {
				template = await this.renderFacilites();
				break;
			}
			default: {
				console.error(`This step does not exist.`);
				return;
			}
		}

		if(template) {
			this.activeData.step = step;
			Manipulator.setDataAttribute(this._resultContainer, 'step', step);

			this._resultContainer.innerHTML = '';
			Manipulator.elementAppend(template, this._resultContainer);

			setTimeout(() => {
				this._pendingMode(false);
			}, 500);
		} else {
			setTimeout(() => {
				this._pendingMode(false);
			}, 500);
		}
	}
}

async function getJson(url) {
	const result = await fetch(url);
	if (!result.ok) {
		throw new Error(`HTTP ${result.status} ${result.statusText}`);
	}

	let data = await result.text();

	return JSON.parse(data);
}

// -------
// Public
// -------

/**
 * init Facility Filter
 */
const init = () => {
	const CONTAINER = SelectorEngine.findOne(`#${NAME}-filter`);

	if(!CONTAINER) {
		return;
	}

	const API_FILTER = new GlobalFacilityFilter(CONTAINER);
};

// Execute
window.addEventListener('load', function () {
	init();
});